import _ from "lodash";
import React from "react";
import classes from "./Textview.scss";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import classNames from "classnames";
import TheatreModeV2 from "UIComponents/TheatreModeV2";
import {
  getTypeDetailsFromUrl,
  extractMediaAttachments,
  getTestCloudUrls,
  replaceUnsignedUrls,
  normalizeUrl,
} from "Worksheet/modules/WorksheetUtils";
import { getStringAutoLinks, getStyleStrippedText } from "Utils";
import { getSignedUrls } from "Worksheet/modules/WorksheetModules";

class Textview extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.textContainer = null;
    this.state = {
      showMediaModal: false,
      mediaViewerData: {},
    };
  }

  componentDidMount() {
    if (this.textContainer) {
      this.textContainer.setAttribute("dir", "auto");
      if (this.props.showMediaLinkInTheatreMode) {
        setTimeout(() => {
          this.addIframeOverlays();
        }, 1000);
      }
    }
  }

  getAttachmentData = async () => {
    const { value, getSignedUrls } = this.props;
    const content = this.isHTML(value) ? this.removeEmptyLines(value) : value;
    const rawAttachments = extractMediaAttachments(content);
    const rawUrls = _.map(rawAttachments, "url");
    // Create a hashmap with key as index and value as url for testcloud.toddleapp.com URLs only
    const urlHashmap = getTestCloudUrls(rawUrls);

    const signedUrls = await getSignedUrls({
      urls: _.values(urlHashmap),
    });

    // Create a new array of URLs, replacing those in the hashmap with their signed versions
    const processedUrls = replaceUnsignedUrls({
      rawAttachments,
      signedUrls,
      urlHashmap,
    });

    return _.reduce(
      processedUrls,
      (attachments, attachment) => {
        const { url, name } = attachment;
        const typeDetails = getTypeDetailsFromUrl(url);

        attachments.push({
          url,
          ...typeDetails,
          name,
        });

        return attachments;
      },
      []
    );
  };

  onClick = async (event, iframe = null) => {
    const element = event?.target;
    const tagName = _.get(element, "tagName", "").toLowerCase();
    // Handle link clicks to prevent event bubbling
    if (tagName === "a") {
      event.stopPropagation();
    }

    if (this.props.showMediaLinkInTheatreMode) {
      // Only process media viewer logic if enabled
      event.preventDefault();
      let url = null;
      url = element?.closest("a")?.href;

      if (iframe) {
        url = iframe?.src;
      }

      if (!url) return;

      // Get attachment data
      const attachments = await this.getAttachmentData();
      if (!attachments.length) return;

      // Normalize the clicked URL
      const normalizedClickedUrl = normalizeUrl(url);

      // Find matching attachment
      const activeAttachmentIndex = _.findIndex(
        attachments,
        attachment => normalizeUrl(attachment.url) === normalizedClickedUrl
      );

      // If we found a matching attachment, show the media viewer
      if (activeAttachmentIndex !== -1) {
        event.preventDefault();
        this.setState({
          showMediaModal: true,
          mediaViewerData: {
            attachments,
            activeAttachmentIndex,
          },
        });
      }
    }
  };

  onMouseUp = event => {
    // Handle video clicks to prevent event bubbling
    if (_.get(event, "target.tagName", "").toLowerCase() === "video") {
      event.stopPropagation();
    }
  };

  isHTML = content => {
    const htmlRegex = /<[a-z][\s\S]*>/i;
    return htmlRegex.test(content);
  };

  removeEmptyLines = htmlContent => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;

    //Each line generated by the editor generates a paragraph with the class tde-paragraph(rte).
    //So using the above the selector we are able to select all the lines of the editor in a line by line order
    const paragraphs = tempDiv.querySelectorAll("p.tde-paragraph");

    // Function to recursively trim the text node
    const trimTextNode = ({ node, type }) => {
      const nodeType = _.get(node, "nodeType", "");
      const isFirst = _.isEqual(type, "FIRST");
      if (nodeType === Node.TEXT_NODE) {
        node.textContent = isFirst
          ? _.trimStart(node.textContent)
          : _.trimEnd(node.textContent);
        return true;
      } else if (nodeType === Node.ELEMENT_NODE && node.hasChildNodes()) {
        const childNode = isFirst ? node.firstChild : node.lastChild;
        return trimTextNode({ node: childNode, type });
      }
      return false;
    };

    // Iterate from the beginning
    for (let i = 0; i < paragraphs.length; i++) {
      const paragraph = paragraphs[i];

      // Remove <br> and empty tags from the start
      while (
        paragraph.firstChild &&
        (paragraph.firstChild.tagName === "BR" ||
          (paragraph.firstChild.tagName === "SPAN" &&
            _.isEmpty(paragraph.firstChild.textContent.trim())))
      ) {
        paragraph.removeChild(paragraph.firstChild);
      }

      // If the paragraph has content, stop iterating from the beginning
      if (paragraph.hasChildNodes()) {
        trimTextNode({ node: paragraph.firstChild, type: "FIRST" });
        break;
      }
    }

    // Iterate from the end
    for (let j = paragraphs.length - 1; j >= 0; j--) {
      const paragraph = paragraphs[j];

      // Remove <br> and empty tags from the end
      while (
        paragraph.lastChild &&
        (paragraph.lastChild.tagName === "BR" ||
          (paragraph.lastChild.tagName === "SPAN" &&
            _.isEmpty(paragraph.lastChild.textContent.trim())))
      ) {
        paragraph.removeChild(paragraph.lastChild);
      }

      // If the paragraph has content, stop iterating from the end
      if (paragraph.hasChildNodes()) {
        trimTextNode({ node: paragraph.lastChild, type: "LAST" });
        break;
      }
    }

    // Remove paragraphs with no children
    _.forEach(paragraphs, paragraph => {
      if (!paragraph.hasChildNodes()) {
        paragraph.parentNode.removeChild(paragraph);
      }
    });
    return tempDiv.innerHTML;
  };

  toggleMediaModal = () => {
    this.setState({ showMediaModal: false, mediaViewerData: {} });
  };

  addIframeOverlays = () => {
    if (!this.textContainer) return;

    // Remove existing wrappers and overlays
    const existingWrappers = this.textContainer.querySelectorAll(
      ".iframe-wrapper"
    );
    existingWrappers.forEach(wrapper => {
      const iframe = wrapper.querySelector("iframe");
      if (iframe) wrapper.replaceWith(iframe);
    });

    const iframes = this.textContainer.querySelectorAll('iframe[src*=".pdf"]');

    iframes.forEach(iframe => {
      // Create wrapper
      const wrapper = document.createElement("div");
      wrapper.className = "iframe-wrapper";
      wrapper.style.cssText = `
        position: relative;
        display: grid;
      `;

      // Insert wrapper before iframe and move iframe inside
      iframe.parentNode.insertBefore(wrapper, iframe);
      wrapper.appendChild(iframe);

      // SVG icon container
      const icon = document.createElement("div");
      icon.style.cssText = `
        position: absolute;
        bottom: 8px;
        left: 12px;
        z-index: 2;
        cursor: pointer;
        border: 1px solid black;
        box-shadow: white 0px 0px 0px 2px;
        display: grid;
        height: 16px;
        width: 16px;
      `;

      icon.innerHTML = `
        <svg viewBox="0 0 100 100"
          class="fill-current w-5 h-5 text-iconDefault"
          aria-hidden="true">
          <path d="M36.585 32.165a3.125 3.125 0 11-4.42 4.42L18.75 23.169v10.313a3.125 3.125 0 11-6.25 0V15.625c0-1.726 1.4-3.125 3.125-3.125h17.857a3.125 3.125 0 110 6.25H23.17l13.416 13.415zM36.585 67.835a3.125 3.125 0 10-4.42-4.42L18.75 76.831V66.518a3.125 3.125 0 10-6.25 0v17.857c0 1.726 1.4 3.125 3.125 3.125h17.857a3.125 3.125 0 100-6.25H23.17l13.416-13.415zM63.415 63.415a3.125 3.125 0 000 4.42L76.831 81.25H66.518a3.125 3.125 0 100 6.25h17.857c1.726 0 3.125-1.4 3.125-3.125V66.518a3.125 3.125 0 10-6.25 0V76.83L67.835 63.415a3.125 3.125 0 00-4.42 0zM63.415 32.165a3.125 3.125 0 104.42 4.42L81.25 23.169v10.313a3.125 3.125 0 106.25 0V15.625c0-1.726-1.4-3.125-3.125-3.125H66.518a3.125 3.125 0 100 6.25H76.83L63.415 32.165z"></path>
        </svg>
      `;

      icon.querySelector("svg").style.cssText = `
        width: 100%;
        height: 100%;
      `;

      icon.addEventListener("click", event => {
        event.preventDefault();
        event.stopPropagation();
        this.onClick(event, iframe);
      });

      wrapper.appendChild(icon);
    });
  };

  renderView = () => {
    const {
      value,
      customTextStyle,
      applyDefaultClass = true,
      isWordWrapRequired = false, // This prop is required for word wrap for text area elements
      isRtlAlignmentRequired = false,
      sanitizationType = "DOCUMENT_EDITOR",
      dsVersion,
      isInactiveQuestionRevamp = false,
      isSinglePageAndNewUnitView,
      shouldInheritFontSize = false,
    } = this.props;

    const isDSV2 = dsVersion === "2.0";

    const { showMediaModal, mediaViewerData } = this.state;
    const { attachments, activeAttachmentIndex } = mediaViewerData;
    const content = this.isHTML(value) ? this.removeEmptyLines(value) : value;
    const richTextClasses = classNames({
      [classes.richText]: applyDefaultClass,
      [classes.customRichText]: !applyDefaultClass,
      [classes.whiteSpacePreWrap]: isWordWrapRequired,
      [classes.richTextAlignmentByDirAttr]: isRtlAlignmentRequired,
      [classes.inactiveRichTextV2]: isInactiveQuestionRevamp,
      [classes.richTextWithNoFontSize]:
        shouldInheritFontSize ||
        (applyDefaultClass && isSinglePageAndNewUnitView),
    });

    return (
      <>
        <div
          className={classNames({
            [classes.detailText]: !isDSV2 && !isSinglePageAndNewUnitView,
            ["text-body-1"]: !isDSV2 && !isSinglePageAndNewUnitView,
            "text-body": isDSV2 || isSinglePageAndNewUnitView,
            [classes.detailTextV2]: isDSV2 || isSinglePageAndNewUnitView,
          })}
          onClick={this.onClick}
          onMouseUp={this.onMouseUp}
          style={customTextStyle}
          ref={ref => (this.textContainer = ref)}
        >
          {Array.isArray(value) ? (
            _.join(value, ", ")
          ) : (
            <div
              className={richTextClasses}
              dangerouslySetInnerHTML={{
                __html: getStyleStrippedText({
                  text: getStringAutoLinks(content),
                  type: sanitizationType,
                }),
              }}
            />
          )}
        </div>
        {showMediaModal && (
          <TheatreModeV2
            mediaAttachments={{
              attachments,
            }}
            activeAttachmentIndex={activeAttachmentIndex}
            toggleTheatreMode={this.toggleMediaModal}
            isWorksheet={true}
          />
        )}
      </>
    );
  };
}

// Textview.defaultProps = {
//   applyDefaultClass: true
// };
const mapActionCreators = { getSignedUrls };

export default compose(connect(null, mapActionCreators))(Textview);
